import React, { useEffect, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useTheme,
  AppBar,
  Container,
  Toolbar,
  type ToolbarTypeMap,
  Stack,
  Box,
  IconButton,
  useMediaQuery,
  Button,
  styled,
} from '@mui/material';
import { type OverridableComponent } from '@mui/material/OverridableComponent';
import { Logo, CallCenterLink } from './components';
import { ReactComponent as MenuIcon } from 'assets/images/menu.svg';
import { ReactComponent as LoginIcon } from 'assets/images/login-icon.svg';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { openSidebar, selectUi, setAllFeatureFlags } from '../../store/slices/ui';
import { useFeatureFlags, useMenuItems } from 'hooks';
import { CustomLink } from './components/Link';
import { RouterUrl } from 'routes';
import { isAlreadyAuthorized } from 'utils';

const MobileMenu = lazy(async () => await import('./components/MobileMenu'));

interface HeaderHeightProps {
  headerHeight: string;
}

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: prop => prop !== 'headerHeight',
})<HeaderHeightProps>(({ theme, headerHeight }) => ({
  backgroundColor: '#fefefe',
  border: 'none',
  borderBottom: '1px solid #f7f7fa',
  zIndex: theme.zIndex.drawer - 1,
  height: headerHeight,
}));

const HeaderToolbar = styled(Toolbar, {
  shouldForwardProp: prop => prop !== 'headerHeight',
})<HeaderHeightProps>(({ headerHeight }) => ({
  height: headerHeight,
  maxHeight: headerHeight,
  minHeight: 'auto',
})) as OverridableComponent<ToolbarTypeMap<HeaderHeightProps>>;

const NavLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '10px',
  marginLeft: '10px',
  [theme.breakpoints.up('lg')]: {
    gap: '30px',
    marginLeft: '30px',
  },
}));

const CallCenterWrapper = styled(Box)(() => ({
  flexGrow: 1,
}));

const LoginActionButton = styled(Button)(() => ({
  padding: '6px 12px',
  borderRadius: '12px',
  backgroundColor: '#f1f7fc',
  gap: '8px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#071B3A',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: '#e9e9e9',
  },
}));

const MenuButton = styled(IconButton)(() => ({
  flexGrow: 1,
}));

export const Header = () => {
  const { isSidebarOpened, hideLinksForTestUser } = useAppSelector(selectUi);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { menuItems } = useMenuItems('web');
  const { pathname } = useLocation();
  const { flags } = useFeatureFlags(pathname.includes('/f/') || !isAlreadyAuthorized());
  const accountPath = pathname.includes('account');
  const reclamationPath = pathname.includes('reclamation');

  const headerHeight = accountPath || isTablet ? '50px' : '80px';

  useEffect(() => {
    if (flags) {
      dispatch(setAllFeatureFlags(flags));
    }
  }, [flags]);

  const handleMenuToggle = () => {
    dispatch(openSidebar(!isSidebarOpened));
  };

  const handleLogin = () => {
    navigate(RouterUrl.Login, { replace: true });
  };

  return (
    <>
      {!accountPath || reclamationPath || (accountPath && isTablet) ? (
        <>
          <StyledAppBar position="static" variant="outlined" elevation={0} headerHeight={headerHeight}>
            <Container>
              <HeaderToolbar disableGutters component={Stack} flexDirection="row" headerHeight={headerHeight}>
                <Logo />
                {!hideLinksForTestUser && reclamationPath && !(reclamationPath && isLargeTablet) && (
                  <NavLinks>
                    {menuItems.map(link => (
                      <CustomLink link={link} key={link.link} size="small" />
                    ))}
                  </NavLinks>
                )}
                <Stack flexDirection="row" justifyContent="space-between" alignItems="center" gap={2} ml="auto">
                  {!(reclamationPath && isLargeTablet) && (
                    <CallCenterWrapper>
                      <CallCenterLink />
                    </CallCenterWrapper>
                  )}
                  {reclamationPath && (
                    <LoginActionButton size="small" onClick={handleLogin}>
                      <LoginIcon style={{ flexShrink: 0 }} />
                      Acceder
                    </LoginActionButton>
                  )}

                  {(accountPath || (reclamationPath && isLargeTablet)) && (
                    <MenuButton
                      size="large"
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                      onClick={handleMenuToggle}
                      color="inherit">
                      <MenuIcon />
                    </MenuButton>
                  )}
                </Stack>
              </HeaderToolbar>
            </Container>
          </StyledAppBar>
          {(accountPath || (reclamationPath && isLargeTablet)) && (
            <Suspense fallback={null}>
              <MobileMenu open={isSidebarOpened} onToggle={handleMenuToggle} inAccount={accountPath} />
            </Suspense>
          )}
        </>
      ) : null}
    </>
  );
};
